import {
  BASE_COINS_PER_HOUR,
  CoinLineItem,
  HAPPY_HOUR,
  isThursdayCheckIn,
  LAST_ORDERS,
  LAST_OUT_PRIZE,
  MAX_NIGHT_POWERUP_EXTRA,
  MAX_POWERUP_MULTIPLIER,
  MYSTERY_PINT_ODDS,
  PUB_CRAWL,
  PUB_GOLF,
  SNOWBALL,
  STICK_AROUND_HOURS,
  summariseNight,
  THE_WHIP,
} from "./economy";
import { getShopItem } from "./items";
import { isSameVenue } from "./thursday-time";
import { ActivePowerup, Session, UserProfile } from "./types";

/**
 * Power-ups: util items that change how you earn for a while, for better or
 * worse depending on how the night goes. They're stored on the session they
 * were bought on (`doubleCoin` and `powerups`). Snowball and Mystery Pint
 * change your hourly coin, so their earnings can always be worked out from
 * the session alone: by the backend when it banks them and by the frontend
 * as the counter ticks. The rest (Happy Hour, Pub Crawl, Last Orders, The
 * Whip and Pub Golf) depend on where everyone went, who turned up and who
 * stayed out latest, so they're settled the morning after with the rest of
 * the night (see getNightPowerupBonuses).
 * Every power-up is also mirrored onto the profile (`activePowerups`) so the
 * leaderboard can show who's powered up. The numbers live in economy.ts.
 */

const HOUR_MS = 60 * 60 * 1000;

/** Power-ups that change your hourly coin (and so light up your check-in). */
export const EARNING_POWERUP_IDS = [
  "doublecoins",
  "happyhour",
  "snowball",
  "mysterypint",
  "pubcrawl",
  "lastorders",
  "whip",
  "pubgolf",
];

/** Power-ups paid (or charged) the morning after, rather than as you earn. */
const SETTLED_POWERUP_IDS = new Set(["happyhour", "pubcrawl", "lastorders", "whip", "pubgolf"]);

export const PADLOCK_ID = "padlock";

interface TimeLike {
  toDate(): Date;
}

function toMs(time: TimeLike) {
  return time.toDate().getTime();
}

/** The parts of a session power-ups need; curses.ts passes its own shape. */
export interface PowerupSession {
  coinRate: number;
  doubleCoin?: boolean;
  powerups?: { id: string; start: TimeLike; end: TimeLike | null; multiplier?: number }[];
  startTime: TimeLike;
}

export interface PowerupStatus {
  id: string;
  name: string;
  icon: string;
  /** What it's multiplying your hourly coin by right now. */
  multiplier?: number;
  /** When it wears off, if before check-out. */
  endsAt?: Date;
  /** Snowball: when it next grows. */
  growsAt?: Date;
  /** Whether it pays out the morning after (see getNightPowerupBonuses). */
  settlesInMorning?: boolean;
}

function describe(id: string): Pick<PowerupStatus, "id" | "name" | "icon"> {
  const item = getShopItem(id);
  const icon = item?.options?.icon;

  return {
    id,
    // Shop names carry their emoji; the icon is shown separately.
    name: (item?.name ?? id).replace(/\s*\p{Extended_Pictographic}️?$/u, ""),
    icon: typeof icon === "string" ? icon : "✨",
  };
}

function getSnowballMultiplier(hoursIn: number) {
  return Math.min(
    SNOWBALL.startMultiplier + Math.floor(hoursIn) * SNOWBALL.stepMultiplier,
    SNOWBALL.maxMultiplier,
  );
}

/** How many whole hours a snowball takes to reach its maximum. */
const SNOWBALL_STEPS = Math.ceil(
  (SNOWBALL.maxMultiplier - SNOWBALL.startMultiplier) / SNOWBALL.stepMultiplier,
);

/** Rolls a Mystery Pint's multiplier from `roll` in [0, 1). */
export function rollMysteryPint(roll: number) {
  let threshold = 0;

  for (const outcome of MYSTERY_PINT_ODDS) {
    threshold += outcome.chance;

    if (roll < threshold) {
      return outcome.multiplier;
    }
  }

  return MYSTERY_PINT_ODDS[MYSTERY_PINT_ODDS.length - 1].multiplier;
}

/** The ×N each session power-up running at `at` (ms) is worth on its own. */
function getRunningPowerups(session: PowerupSession, at: number) {
  return (session.powerups ?? []).flatMap((powerup) => {
    const start = toMs(powerup.start);
    const end = powerup.end ? toMs(powerup.end) : Infinity;

    if (at < start || at >= end) {
      return [];
    }

    switch (powerup.id) {
      case "snowball":
        return [{ powerup, multiplier: getSnowballMultiplier((at - start) / HOUR_MS) }];
      case "mysterypint":
        return [{ powerup, multiplier: powerup.multiplier ?? 1 }];
      default:
        return [];
    }
  });
}

/**
 * What a session's hourly coin is multiplied by at `at`. Each power-up adds
 * its extra (×3 adds 2, ×0.5 takes off half) and the total is kept between 0
 * and MAX_POWERUP_MULTIPLIER.
 */
export function getPowerupMultiplier(session: PowerupSession, at: Date) {
  const extras =
    (session.doubleCoin ? 1 : 0) +
    getRunningPowerups(session, at.getTime()).reduce(
      (total, { multiplier }) => total + multiplier - 1,
      0,
    );

  return Math.min(Math.max(1 + extras, 0), MAX_POWERUP_MULTIPLIER);
}

/**
 * Every moment a session's power-up multiplier can change: when one starts
 * or ends, or a snowball grows. Between two of these it's constant.
 */
export function getPowerupMarks(session: PowerupSession): number[] {
  return (session.powerups ?? []).flatMap((powerup) => {
    const start = toMs(powerup.start);
    const marks = [start];

    if (powerup.end) {
      marks.push(toMs(powerup.end));
    }

    if (powerup.id === "snowball") {
      for (let step = 1; step <= SNOWBALL_STEPS; step++) {
        marks.push(start + step * HOUR_MS);
      }
    }

    return marks;
  });
}

/**
 * The hourly coin a session earns over its first `seconds`, with its
 * power-ups, adding up the stretches between getPowerupMarks.
 */
export function calculatePowerupEarnings(session: PowerupSession, seconds: number) {
  if (seconds <= 0) {
    return 0;
  }

  const start = toMs(session.startTime);
  const stop = start + seconds * 1000;
  const sorted = [...new Set([start, stop, ...getPowerupMarks(session)])]
    .filter((mark) => mark >= start && mark <= stop)
    .sort((a, b) => a - b);
  let boostedSeconds = 0;

  for (let i = 1; i < sorted.length; i++) {
    const from = sorted[i - 1];
    const to = sorted[i];

    boostedSeconds +=
      ((to - from) / 1000) * getPowerupMultiplier(session, new Date((from + to) / 2));
  }

  return (boostedSeconds * session.coinRate) / 3600;
}

/** The earning power-ups running on a session at `now`, for its card. */
export function getSessionPowerups(session: PowerupSession, now: Date): PowerupStatus[] {
  const statuses: PowerupStatus[] = [];

  if (session.doubleCoin) {
    statuses.push({ ...describe("doublecoins"), multiplier: 2 });
  }

  for (const powerup of session.powerups ?? []) {
    const start = toMs(powerup.start);
    const end = powerup.end ? toMs(powerup.end) : Infinity;

    if (now.getTime() < start || now.getTime() >= end) {
      continue;
    }

    const status: PowerupStatus = describe(powerup.id);

    if (powerup.end) {
      status.endsAt = powerup.end.toDate();
    }

    if (SETTLED_POWERUP_IDS.has(powerup.id)) {
      status.settlesInMorning = true;
    } else {
      const running = getRunningPowerups(session, now.getTime()).find(
        (candidate) => candidate.powerup === powerup,
      );

      if (!running) {
        continue;
      }

      status.multiplier = running.multiplier;

      if (powerup.id === "snowball" && running.multiplier < SNOWBALL.maxMultiplier) {
        const hoursIn = Math.floor((now.getTime() - start) / HOUR_MS);
        status.growsAt = new Date(start + (hoursIn + 1) * HOUR_MS);
      }
    }

    statuses.push(status);
  }

  return statuses;
}

/** The parts of a night's sessions that the morning-settled power-ups are worked out from. */
export type NightPowerupSession = Pick<
  Session,
  | "id"
  | "profileId"
  | "startTime"
  | "endTime"
  | "locationName"
  | "coinRate"
  | "thursday"
  | "powerups"
> & {
  venue?: Pick<Session["venue"], "id"> | null;
  locationRef?: { id: string } | null;
  profile?: Pick<UserProfile, "isBro"> | null;
};

interface Interval {
  start: number;
  end: number;
}

function sessionInterval(session: NightPowerupSession, now: number): Interval {
  return { start: toMs(session.startTime), end: session.endTime ? toMs(session.endTime) : now };
}

function overlapMs(a: Interval, b: Interval) {
  return Math.max(Math.min(a.end, b.end) - Math.max(a.start, b.start), 0);
}

/** The rate a session earns at before power-ups; see withEarningRate. */
function baseRate(session: NightPowerupSession) {
  return isThursdayCheckIn(session) ? session.coinRate : BASE_COINS_PER_HOUR;
}

function pubKey(session: NightPowerupSession) {
  return session.venue?.id || session.locationRef?.id || session.locationName || session.id;
}

function minutes(count: number) {
  return count * 60 * 1000;
}

function pick(table: readonly number[], index: number) {
  return table[Math.min(Math.max(index, 0), table.length - 1)];
}

function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

/** Power-ups that cover the rest of the night, and share MAX_NIGHT_POWERUP_EXTRA. */
const NIGHT_LONG_POWERUP_IDS = new Set(["pubcrawl", "lastorders", "whip", "pubgolf"]);

/** Everything about a player's night since `from` that the night-long power-ups need. */
class NightSince {
  constructor(
    private mine: NightPowerupSession[],
    private everyone: NightPowerupSession[],
    private from: number,
    private now: number,
  ) {}

  /** Each of my check-ins, clipped to start no earlier than `from`. */
  private visits() {
    return this.mine.flatMap((visit) => {
      const interval = sessionInterval(visit, this.now);
      const clipped = { start: Math.max(interval.start, this.from), end: interval.end };

      return clipped.start < clipped.end ? [{ visit, interval: clipped }] : [];
    });
  }

  /** Hourly coin earned since `from`, at the base rate. */
  earned() {
    return this.visits().reduce(
      (total, { visit, interval }) =>
        total + (baseRate(visit) * (interval.end - interval.start)) / HOUR_MS,
      0,
    );
  }

  /** Time spent in each pub since `from`. */
  pubTime() {
    const time = new Map<string, number>();

    for (const { visit, interval } of this.visits()) {
      time.set(pubKey(visit), (time.get(pubKey(visit)) ?? 0) + interval.end - interval.start);
    }

    return time;
  }

  /** Time each other bro spent in the same pub as me since `from`, pub by pub. */
  broTime() {
    const byPub = new Map<string, Map<string, number>>();

    for (const { visit, interval } of this.visits()) {
      const bros = byPub.get(pubKey(visit)) ?? new Map<string, number>();

      for (const other of this.everyone) {
        if (
          other.profileId === visit.profileId ||
          other.profile?.isBro !== true ||
          !isSameVenue(visit, other)
        ) {
          continue;
        }

        const overlap = overlapMs(interval, sessionInterval(other, this.now));
        bros.set(other.profileId, (bros.get(other.profileId) ?? 0) + overlap);
      }

      byPub.set(pubKey(visit), bros);
    }

    return byPub;
  }
}

function happyHour(
  session: NightPowerupSession,
  powerup: NonNullable<NightPowerupSession["powerups"]>[number],
  everyone: NightPowerupSession[],
  now: number,
): CoinLineItem {
  const window = {
    start: toMs(powerup.start),
    end: Math.min(powerup.end ? toMs(powerup.end) : Infinity, sessionInterval(session, now).end),
  };
  const hours = Math.max(window.end - window.start, 0) / HOUR_MS;
  const time = new Map<string, number>();

  for (const other of everyone) {
    if (
      other.profileId === session.profileId ||
      other.profile?.isBro !== true ||
      !isSameVenue(session, other)
    ) {
      continue;
    }

    const overlap = overlapMs(window, sessionInterval(other, now));
    time.set(other.profileId, (time.get(other.profileId) ?? 0) + overlap);
  }

  const company = [...time.values()].filter((ms) => ms >= minutes(HAPPY_HOUR.minMinutes)).length;
  const multiplier = pick(HAPPY_HOUR.byCompany, company);

  return {
    id: `happy-hour-${session.id}`,
    label:
      company === 0
        ? "Happy Hour: drinking alone"
        : `Happy Hour with ${plural(company, "bro")} (×${multiplier})`,
    coin: Math.round(baseRate(session) * hours * (multiplier - 1)),
  };
}

/**
 * Where a player came in the last-out rankings, and against how many others.
 * Anyone who left within LAST_OUT_PRIZE.windowMinutes of you left with you.
 */
function lastOutPlace(mine: NightPowerupSession[], everyone: NightPowerupSession[], now: number) {
  const lastOut = new Map<string, number>();

  for (const session of everyone) {
    const end = sessionInterval(session, now).end;
    lastOut.set(session.profileId, Math.max(lastOut.get(session.profileId) ?? 0, end));
  }

  const profileId = mine[0]?.profileId;
  const myLastOut = Math.max(...mine.map((session) => sessionInterval(session, now).end));
  const rivals = [...lastOut].filter(([id]) => id !== profileId);

  return {
    place:
      1 +
      rivals.filter(([, end]) => end > myLastOut + minutes(LAST_OUT_PRIZE.windowMinutes)).length,
    rivals: rivals.length,
  };
}

function nightLong(
  id: string,
  mine: NightPowerupSession[],
  everyone: NightPowerupSession[],
  night: NightSince,
  now: number,
): { label: string; multiplier: number } | null {
  switch (id) {
    case "pubcrawl": {
      const pubs = [...night.pubTime().values()].filter(
        (ms) => ms >= minutes(PUB_CRAWL.minMinutes),
      ).length;
      const multiplier = pick(PUB_CRAWL.byPubs, pubs - 1);

      return {
        label:
          pubs <= 1
            ? "Pub Crawl: never left the pub"
            : `Pub Crawl: ${plural(pubs, "pub")} (×${multiplier})`,
        multiplier,
      };
    }

    case "lastorders": {
      const { place, rivals } = lastOutPlace(mine, everyone, now);
      const summary = summariseNight("", mine);
      const stuckAround = !!summary && summary.closingMs >= STICK_AROUND_HOURS * HOUR_MS;

      if (rivals < LAST_ORDERS.minRivals) {
        return null;
      }

      const multiplier = stuckAround ? pick(LAST_ORDERS.byPlace, place - 1) : 0.5;

      return {
        label: stuckAround
          ? `Last Orders: ${place === 1 ? "last one standing" : `${ordinal(place)} out`} (×${multiplier})`
          : "Last Orders: didn't stick around",
        multiplier,
      };
    }

    case "whip": {
      const total = new Map<string, number>();

      for (const bros of night.broTime().values()) {
        for (const [bro, ms] of bros) {
          total.set(bro, (total.get(bro) ?? 0) + ms);
        }
      }

      const bros = [...total.values()].filter((ms) => ms >= minutes(THE_WHIP.minMinutes)).length;
      const multiplier = pick(THE_WHIP.byBros, bros);

      return {
        label:
          bros === 0
            ? "The Whip: nobody chipped in"
            : `The Whip: ${plural(bros, "bro")} chipped in (×${multiplier})`,
        multiplier,
      };
    }

    case "pubgolf": {
      const pubTime = night.pubTime();
      const holes = [...night.broTime()].filter(
        ([pub, bros]) =>
          (pubTime.get(pub) ?? 0) >= minutes(PUB_GOLF.minMinutes) &&
          [...bros.values()].some((ms) => ms >= minutes(PUB_GOLF.minMinutes)),
      ).length;
      const multiplier = pick(PUB_GOLF.byHoles, holes);

      return {
        label:
          holes === 0
            ? "Pub Golf: didn't sink a hole"
            : `Pub Golf: ${plural(holes, "hole")} (×${multiplier})`,
        multiplier,
      };
    }

    default:
      return null;
  }
}

function ordinal(place: number) {
  return ["1st", "2nd", "3rd"][place - 1] ?? `${place}th`;
}

/**
 * What a player's morning-settled power-ups (Happy Hour and the night-long
 * ones) paid or cost over a night, as line items for the morning settlement
 * (see economy.ts for the rules). `mine` is the player's sessions that night
 * and `everyone` is everybody's (theirs included); a session that's still
 * open counts as running to `now`.
 */
export function getNightPowerupBonuses(
  mine: NightPowerupSession[],
  everyone: NightPowerupSession[],
  now: Date = new Date(),
): CoinLineItem[] {
  const items: CoinLineItem[] = [];
  const nowMs = now.getTime();
  let firstNightLong = Infinity;
  let nightLongExtra = 0;

  for (const session of mine) {
    for (const powerup of session.powerups ?? []) {
      if (powerup.id === "happyhour") {
        items.push(happyHour(session, powerup, everyone, nowMs));
        continue;
      }

      if (!NIGHT_LONG_POWERUP_IDS.has(powerup.id)) {
        continue;
      }

      const from = toMs(powerup.start);
      const night = new NightSince(mine, everyone, from, nowMs);
      const outcome = nightLong(powerup.id, mine, everyone, night, nowMs);

      if (outcome) {
        const coin = Math.round(night.earned() * (outcome.multiplier - 1));

        items.push({ id: `${powerup.id}-${session.id}`, label: outcome.label, coin });
        firstNightLong = Math.min(firstNightLong, from);
        nightLongExtra += Math.max(coin, 0);
      }
    }
  }

  if (firstNightLong < Infinity) {
    const cap = Math.round(
      MAX_NIGHT_POWERUP_EXTRA * new NightSince(mine, everyone, firstNightLong, nowMs).earned(),
    );

    if (nightLongExtra > cap) {
      items.push({ id: "night-powerup-cap", label: "Power-up cap", coin: cap - nightLongExtra });
    }
  }

  return items.filter((item) => item.coin !== 0);
}

function isActive(powerup: ActivePowerup, profile: Pick<UserProfile, "session">, now: Date) {
  const notExpired = !powerup.expires || powerup.expires.toMillis() > now.getTime();
  const sessionRunning = !powerup.sessionId || powerup.sessionId === profile.session?.id;

  return notExpired && sessionRunning;
}

/** Every power-up a player has running at `now`, for the leaderboard. */
export function getActivePowerups(
  profile: Pick<UserProfile, "activePowerups" | "session">,
  now: Date,
): PowerupStatus[] {
  return Object.entries(profile.activePowerups ?? {})
    .filter(([, powerup]) => isActive(powerup, profile, now))
    .map(([id, powerup]) => ({
      ...describe(id),
      ...(powerup.multiplier === undefined ? {} : { multiplier: powerup.multiplier }),
      ...(powerup.expires ? { endsAt: powerup.expires.toDate() } : {}),
    }));
}

/** Whether a Padlock is keeping a player's coins safe from theft at `now`. */
export function isPadlocked(profile: Pick<UserProfile, "activePowerups" | "session">, now: Date) {
  const padlock = profile.activePowerups?.[PADLOCK_ID];

  return !!padlock && isActive(padlock, profile, now);
}
